import React, { useState, useEffect, useMemo, useRef } from "react";
import AudioPlayBgLeft from "@/assets/summary/audio_player_bg_left.png";
import AudioPlayBgRight from "@/assets/summary/audio_player_bg_right.png";
import AudioPlayIcon from "@/assets/summary/audio_play.svg?react";
import AudioPauseIcon from "@/assets/summary/audio_pause.svg?react";
import Back5Icon from "@/assets/summary/back5.svg?react";
import Forward5Icon from "@/assets/summary/forward5.svg?react";
import UnfoldIcon from "@/assets/summary/unfold.svg";
import FoldIcon from "@/assets/summary/fold.svg";
import MoreIcon from "@/assets/summary/more.svg?react";
import MoreSettingIcon from "@/assets/summary/setting.svg?react";
import { formatRecordDuration } from "@/utils/formatter.ts";
import AudioRateModal from "./components/AudioRateModal";
import MorePanel from "./components/MorePanel";
import MobileMorePanel from "./components/MobileMorePanel";
import AudioPlayerIns from "./AudioPlayerIns";
import { IAudioInfo } from "@/types/summary";
import "./index.css";
import "rc-slider/assets/index.css";
import { message } from "antd";
import fridayReporter from "@/utils/friday";
import AVPlayerHelper from "@/bussiness/AVPlayerHelper";
import { getTotalDiffSeconds } from "@/utils/getTotalDiffSeconds";

interface IProps {
  audioInfo?: IAudioInfo | null;
  scTimeList?: number[]; //[语句1开始，语句1结束，语句2开始，语句2结束，语句3开始，语句3结束]
  onCurrentNumChange?: (currentNum: number) => void;
  isMobile?: boolean;
  onAudioInsLoaded?: (audioIns: AudioPlayerIns) => void;
  onModifyPlayingTime?: (time: number) => void;
  onIsPlayingChange?: (isPlaying: boolean) => void;
}

const AudioPlayer: React.FC<IProps> = (props) => {
  const propsRef = useRef(props);
  propsRef.current = props;
  const { audioInfo, isMobile, scTimeList } = props;
  const [myAudio, setMyAudio] = useState<AudioPlayerIns | null>(null);
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [currentNum, setCurrentNum] = useState<number>(0);
  const [durationNum, setDurationNum] = useState<number>(0);
  const [playRate, setPlayRate] = useState("1.0");
  const [showRateModal, setShowRateModal] = useState(false);
  const [skipBlank, setSkipBlank] = useState(false);
  const [showMorePanel, setShowMorePanel] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const bgImgWidth = useMemo(() => {
    return isMobile ? 40 : 53;
  }, [isMobile]);

  const totalSkipTime = useMemo(() => {
    return getTotalDiffSeconds(durationNum, scTimeList || []);
  }, [scTimeList, durationNum]);

  // 退出页面时，暂停播放音频
  useEffect(() => {
    return () => {
      console.log("audioPlayer unmount");
      myAudio?.pause();
      AVPlayerHelper.pauseAudio();
    };
  }, [myAudio?.pause]);

  useEffect(() => {
    const { onCurrentNumChange, onModifyPlayingTime, onAudioInsLoaded, onIsPlayingChange } = propsRef.current;
    if (!audioInfo) return;
    console.log("audioInfo", audioInfo);
    const { audioDuration, audioSrc } = audioInfo;
    let tempAudio: AudioPlayerIns | null = new AudioPlayerIns({
      audioDuration,
      audioSrc,
      onIsPlayingChange: (isPlaying: boolean) => {
        setPlaying(isPlaying);
        onIsPlayingChange?.(isPlaying);
      },
      onCurrentNumChange: (currentNum: number) => {
        setCurrentNum(currentNum);
        onCurrentNumChange && onCurrentNumChange(currentNum);
      },
      onDurationNumChange: (durationNum: number) => {
        setDurationNum(durationNum);
      },
      onModifyPlayingTime,
    });
    setMyAudio(tempAudio);
    onAudioInsLoaded && onAudioInsLoaded(tempAudio);
    return () => {
      tempAudio?.destroy();
      tempAudio = null;
    };
  }, [audioInfo]);

  useEffect(() => {
    if (!myAudio || !scTimeList) return;
    myAudio.setScTimeList(scTimeList);
  }, [scTimeList, myAudio]);

  // 播放暂停
  const toggle = () => {
    if (!myAudio) return;
    if (!isPlaying) {
      fridayReporter.pushData({
        eventCode: 'summary_h5_play'
      })
    }
    myAudio.toggle();
  };

  useEffect(() => {
    setSliderValue(durationNum ? (currentNum / durationNum) : 0);
  }, [durationNum, currentNum]);


  const handleSliderChange = (val: number | number[], seek?: boolean) => {
    if (!audioInfo) return;
    if (typeof val !== "number") return;
    setSliderValue(val);
    const time = val * durationNum;
    if (myAudio && seek) {
      myAudio.setCurrentTime(time);
      AVPlayerHelper.changeSliderValueCb();
    }
  };

  const handleChangePlayRate = (playRate: string) => {
    if (!myAudio) return;
    myAudio.setPlayRate(Number(playRate));
    setPlayRate(playRate);
  };

  const handleChangeSkipBlank = (skipBlank: boolean) => {
    if (!myAudio) return;
    myAudio.setSkipBlank(skipBlank);
    setSkipBlank(skipBlank);
    // 弹toast 提示
    if (skipBlank) {
      message.success(`已跳过${totalSkipTime}`);
    }
  };

  const handleBack5s = () => {
    if (!myAudio || currentNum < 0) return;
    const newNum = currentNum - 5 < 0 ? 0 : currentNum - 5;
    myAudio.setCurrentTime(newNum);
    AVPlayerHelper.changeSliderValueCb();
  };

  const handleForward5s = () => {
    if (!myAudio || currentNum > durationNum) return;
    const newNum = currentNum + 5 > durationNum ? durationNum : currentNum + 5;
    myAudio.setCurrentTime(newNum);
    AVPlayerHelper.changeSliderValueCb();
  };

  const handleShowMobileMorePanel = () => {
    if (!audioInfo) return;
    setShowMorePanel(true);
  };

  const handleChangeTime = (e: React.MouseEvent<HTMLProgressElement>) => {
    const progress = e.nativeEvent.offsetX / e.currentTarget.offsetWidth;
    handleSliderChange(progress, true);
  };

  const cursorCss = audioInfo ? "cursor-pointer" : "cursor-default";

  return (
    <>
      <div className={`h-20 rounded-4 relative ${!audioInfo && "op-60"} shadow-[0_0_16px_0_rgba(0,0,0,0.08)]`}>
        <div className="absolute flex items-end w-full">
          <img src={AudioPlayBgLeft} className={`h-20 w-${bgImgWidth}`} />
          <div className={`flex-1  h-19.9 bg-white`}></div>
          <img src={AudioPlayBgRight} className={`w-${bgImgWidth} h-20`} />
        </div>
        {!isMobile && (
          <div className="absolute top-2 left-3 text-3 text-mx-tertiary flex items-center">
            <AudioRateModal visible={showRateModal && !!audioInfo} setVisible={setShowRateModal} playRate={playRate} setPlayRate={handleChangePlayRate}>
              <div className={`flex items-center ${cursorCss}`}>
                {`${playRate}X`}
                {showRateModal ? <img src={FoldIcon} className="w-4 h-4 ml-1" /> : <img src={UnfoldIcon} className="w-4 h-4 ml-1" />}
              </div>
            </AudioRateModal>
          </div>
        )}
        <div className={`absolute right-3 ${isMobile ? 'top-1.5' : 'top-2'}`}>
          {isMobile ? (
            <MoreSettingIcon className="w-6 h-6" onClick={handleShowMobileMorePanel} />
          ) : (
            <MorePanel visible={showMorePanel && !!audioInfo} setVisible={setShowMorePanel} skipBlank={skipBlank} setSkipBlank={handleChangeSkipBlank}>
              <MoreIcon className={`w-6 h-6 ${cursorCss}`} />
            </MorePanel>
          )}
        </div>
        <div className="absolute top-4 left-50% translate-x--50% flex items-center">
          <Back5Icon className={`w-6 h-6 ${cursorCss} tap-highlight-transparent`} onClick={handleBack5s} />

          {isPlaying ? (
            <AudioPauseIcon className={`w-8 h-8 mx-6 ${cursorCss} tap-highlight-transparent`} onClick={toggle} />
          ) : (
            <AudioPlayIcon className={`w-8 h-8 mx-6 ${cursorCss} tap-highlight-transparent`} onClick={toggle} />
          )}
          <Forward5Icon className={`w-6 h-6 ${cursorCss} tap-highlight-transparent`} onClick={handleForward5s} />
        </div>
        <div className="absolute top-10 left-3 right-3 flex flex-col text-3 text-#666 font-400 line-height-5.5">
          <div className="flex justify-between">
            <span>{formatRecordDuration((sliderValue) * durationNum, durationNum)}</span>
            <span>{formatRecordDuration(durationNum, durationNum)}</span>
          </div>
          <progress
            className="audio-progress"
            value={sliderValue}
            onClick={handleChangeTime}
          ></progress>
        </div>
      </div>
      {isMobile && (
        <MobileMorePanel
          visible={showMorePanel}
          playRate={playRate}
          setPlayRate={handleChangePlayRate}
          skipBlank={skipBlank}
          setSkipBlank={handleChangeSkipBlank}
          setVisible={setShowMorePanel}
        />
      )}
    </>
  );
};

export default AudioPlayer;
